import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, inject, input, viewChild } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CdkMediaWatcherService } from '../../utils/media-watcher.service';
import { CdkNavigationService } from '@cdk/components/navigation/navigation.service';
import { RouterLink } from '@angular/router';
import { MatFormField } from '@angular/material/form-field';
import { CdkScrollbarDirective } from '../../directives/cdk-scrollbar/cdk-scrollbar.directive';
import { MatLine } from '@angular/material/core';
import {MatIconAnchor, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatTooltip} from "@angular/material/tooltip";
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";
import {MatInput} from "@angular/material/input";
import {MatDivider} from "@angular/material/divider";
import {MatListItem, MatNavList} from "@angular/material/list";

@Component({
    selector: 'cdk-shortcuts',
    templateUrl: './shortcuts.component.html',
    styleUrls: ['./shortcuts.component.scss'],
    imports: [MatIconButton, MatIcon, MatIconAnchor, MatTooltip, RouterLink, MatMenuTrigger, MatMenu, MatFormField, MatInput, MatDivider, MatNavList, CdkScrollbarDirective, MatListItem, MatLine],
    providers: [
        CookieService
    ]
})

export class CdkShortcutsComponent implements OnInit, AfterViewInit, OnDestroy {
    private _cookieService = inject(CookieService);
    private _cdkMediaWatcherService = inject(CdkMediaWatcherService);
    private _cdkNavigationService = inject(CdkNavigationService);
    private _renderer = inject(Renderer2);

    shortcutItems: any[];
    navigationItems: any[];
    filteredNavigationItems: any[];
    searching: boolean;
    mobileShortcutsPanelActive: boolean;

    readonly navigation = input<any>(undefined);

    // @ts-ignore
    readonly searchInputField = viewChild('searchInput', { read: ElementRef<HTMLInputElement> });

    readonly shortcutsEl = viewChild<ElementRef>('shortcuts');

    // Private
    private _unsubscribeAll: Subject<any>;

    /**
     * Constructor
     *
     */
    constructor() {
        // Set the defaults
        this.shortcutItems = [];
        this.searching = false;
        this.mobileShortcutsPanelActive = false;

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the navigation items and flatten them
        this.filteredNavigationItems = this.navigationItems = this._cdkNavigationService.getFlatNavigation(this.navigation());

        if (this._cookieService.check('CDK2.shortcuts')) {
            this.shortcutItems = JSON.parse(this._cookieService.get('CDK2.shortcuts'));
        } else {
            // User's shortcut items
            this.shortcutItems = [
                {
                    title: 'Calendar',
                    type: 'item',
                    icon: 'today',
                    url: '/apps/calendar'
                },
                {
                    title: 'Mail',
                    type: 'item',
                    icon: 'email',
                    url: '/apps/mail'
                },
                {
                    title: 'Contacts',
                    type: 'item',
                    icon: 'account_box',
                    url: '/apps/contacts'
                },
                {
                    title: 'To-Do',
                    type: 'item',
                    icon: 'check_box',
                    url: '/apps/todo'
                }
            ];
        }

    }

    ngAfterViewInit(): void {
        // Subscribe to media changes
        this._cdkMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                if (matchingAliases.includes('gt-sm')) {
                    this.hideMobileShortcutsPanel();
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(true);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Search
     *
     * @param event
     */
    search(event): void {
        const value = event.target.value.toLowerCase();

        if (value === '') {
            this.searching = false;
            this.filteredNavigationItems = this.navigationItems;

            return;
        }

        this.searching = true;

        this.filteredNavigationItems = this.navigationItems.filter(navigationItem => navigationItem.title.toLowerCase().includes(value));
    }

    /**
     * Toggle shortcut
     *
     * @param event
     * @param itemToToggle
     */
    toggleShortcut(event, itemToToggle): void {
        event.stopPropagation();

        for (let i = 0; i < this.shortcutItems.length; i++) {
            if (this.shortcutItems[i].url === itemToToggle.url) {
                this.shortcutItems.splice(i, 1);

                // Save to the cookies
                this._cookieService.set('CDK2.shortcuts', JSON.stringify(this.shortcutItems));

                return;
            }
        }

        this.shortcutItems.push(itemToToggle);

        // Save to the cookies
        this._cookieService.set('CDK2.shortcuts', JSON.stringify(this.shortcutItems));
    }

    /**
     * Is in shortcuts?
     *
     * @param navigationItem
     * @returns
     */
    isInShortcuts(navigationItem): any {
        return this.shortcutItems.find(item => item.url === navigationItem.url);
    }

    /**
     * On menu open
     */
    onMenuOpen(): void {
        setTimeout(() => {
            this.searchInputField().nativeElement.focus();
        });
    }

    /**
     * Show mobile shortcuts
     */
    showMobileShortcutsPanel(): void {
        this.mobileShortcutsPanelActive = true;
        this._renderer.addClass(this.shortcutsEl().nativeElement, 'show-mobile-panel');
    }

    /**
     * Hide mobile shortcuts
     */
    hideMobileShortcutsPanel(): void {
        this.mobileShortcutsPanelActive = false;
        this._renderer.removeClass(this.shortcutsEl().nativeElement, 'show-mobile-panel');
    }
}
